import React, { useContext, useState } from 'react';
import { Dimensions } from 'react-native';
import {
  NativeBaseProvider,
  ScrollView,
  useToast,
  Button,
  VStack,
  HStack,
  Box,
} from 'native-base';
import {
  Dialog, Portal,
} from 'react-native-paper';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import {
  date, object, string,
  array,
} from 'yup';
import { ModalContext } from '../../context/ModalContext';
import { CreateEvent } from '../../gql/event/mutation';
import { FormStepOne, FormStepTwo } from './StepsFormCreateEvent';
import { FindManyEvents } from '../../gql/event/query';

const steps = [
  {
    id: 'StepOne',
    component: FormStepOne,
    initialValues: {
      name: '',
      type: '',
      location: '',
      startAt: new Date(Date.now()),
      endAt: new Date(Date.now()),
    },
    validationSchema: object({
      name: string().required('Champ requis'),
      location: string().required('Champ requis'),
      type: object().required('Champ requis'),
      startAt: date()
        .required('Champ requis')
        .min(new Date(Date.now()), 'Impossible de mettre une date antérieure'),
      endAt: date()
        .typeError('La date de fin est requise')
        .required('La date de fin est requise')
        .when('startAt', (startAt, schema) => {
          if (startAt) {
            return schema.min(startAt, 'La date de fin doit être après la date de début');
          }
          return schema;
        }),
    }),
  },
  {
    id: 'StepTwo',
    component: FormStepTwo,
    initialValues: {
      media: undefined,
      infoMedia: undefined,
      level: '',
      categories: [],
      registerEndAt: new Date(Date.now()),
    },
    validationSchema: object({
      registerStartAt: date()
        .typeError("La date d'inscription est requise")
        .required("La date d'inscription est requise"),
      registerEndAt: date()
        .typeError("La date limite d'inscription est requise")
        .required("La date limite d'inscription est requise")
        .when('registerStartAt', (startAt, schema) => {
          if (startAt) {
            return schema.min(startAt, 'La date de fin d\'inscription doit être après la date de début d\'inscription');
          }
          return schema;
        }),
      categories: array().min(1, 'Il faut ajouter au moins une catégorie.'),
      level: object().required('Champ requis'),
    }),
  },
];

function Event() {
  const {
    displayCreateEventModal,
    toggleCreateEventModal,
  } = useContext(ModalContext);
  const [createEvent] = useMutation(CreateEvent);
  const toast = useToast();
  const [step, setStep] = useState(0);

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = (values) => {
    if (isLastStep) {
      handleSubmit(values);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (values) => {
    await createEvent({
      variables: {
        name: values.name,
        location: values.location,
        startAt: values.startAt,
        registerStartAt: values.registerStartAt,
        registerEndAt: values.registerEndAt,
        endAt: values.endAt,
        media: values.media,
        infoMedia: values.infoMedia,
        level: values.level?.id,
        type: values.type?.id,
        categories: values.categories.map((c) => c.item),
      },
      refetchQueries: [
        FindManyEvents, // DocumentNode object parsed with gql
        'findManyEvents', // Query name
      ],
    });
    toggleCreateEventModal();
    toast.show('Evènement créé avec succès');
  };

  return (
    <Dialog
      visible={displayCreateEventModal}
      dismissable
      onDismiss={toggleCreateEventModal}
      style={{ maxHeight: 0.8 * Dimensions.get('window').height }}
    >
      <Dialog.ScrollArea>
        <ScrollView contentContainerStyle={{ paddingHorizontal: 24 }}>
          <Formik
            initialValues={steps.reduce((acc, curr) => ({ ...acc, ...curr.initialValues }), {})}
            validationSchema={currentStep.validationSchema}
            onSubmit={handleNext}
          >
            {({
              handleSubmit: formikSubmit, values, errors, touched,
            }) => {
              const CurrentComponent = currentStep.component;
              return (
                <VStack space={4}>
                  <CurrentComponent values={values} errors={errors} touched={touched} />
                  <HStack justifyContent="space-between" mt={4}>
                    {step > 0 && <Button onPress={handleBack}>Précédent</Button>}
                    <Box />
                    <Button onPress={formikSubmit}>{isLastStep ? 'Terminer' : 'Suivant'}</Button>
                  </HStack>
                </VStack>
              );
            }}
          </Formik>
        </ScrollView>
      </Dialog.ScrollArea>
    </Dialog>
  );
}

export default function EventModal() {
  return (
    <Portal>
      <NativeBaseProvider>
        <Event />
      </NativeBaseProvider>
    </Portal>
  );
}
